/**
 * Design-system Button. Variant + size map to the styles in
 * design-system.css (`.btn`, `.btn-primary`, etc.) - keeping the visual
 * source-of-truth in one place.
 */
import { type ButtonHTMLAttributes, type MouseEvent, type ReactNode, forwardRef } from 'react';

import { Loader } from '@/components/ui/loader/loader';

import styles from './button.module.css';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'link' | 'social';
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  fullWidth?: boolean;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  isLoading?: boolean;
  /** Add the AI-sparkle marker (gold ✦) - reserved for AI-trigger buttons. */
  aiSpark?: boolean;
  /**
   * Why the button is blocked. When set together with `disabled`, the
   * button renders "soft-disabled": `aria-disabled` instead of the
   * native `disabled` attribute, the reason exposed as a hover `title`,
   * and clicks suppressed. Native `disabled` swallows pointer events,
   * so a `title` on a natively-disabled button never shows on hover -
   * this keeps the explanation discoverable. A disabled button SHOULD
   * tell the user why; pass this whenever the reason is known.
   */
  disabledReason?: string;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'primary',
    size = 'md',
    fullWidth = false,
    leftIcon,
    rightIcon,
    isLoading = false,
    aiSpark = false,
    className,
    children,
    disabled,
    disabledReason,
    title,
    onClick,
    type,
    ...rest
  },
  ref,
) {
  // "Soft-disabled": the caller wants the button blocked AND has a
  // reason to show. Use aria-disabled (not the native attribute) so the
  // browser still delivers hover events and the `title` tooltip shows.
  // `isLoading` always uses the native disabled - there is no reason to
  // surface, and it must hard-block submits.
  const isDisabled = disabled === true || isLoading;
  const softDisabled = isDisabled && !isLoading && Boolean(disabledReason);

  const classes = [
    styles.btn,
    styles[`variant-${variant}`],
    styles[`size-${size}`],
    fullWidth ? styles.fullWidth : '',
    aiSpark ? styles.aiSpark : '',
    isLoading ? styles.loading : '',
    softDisabled ? styles.softDisabled : '',
    className ?? '',
  ]
    .filter(Boolean)
    .join(' ');

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    if (softDisabled) {
      // aria-disabled does not block clicks on its own - suppress here.
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    onClick?.(event);
  };

  return (
    <button
      ref={ref}
      type={type ?? 'button'}
      className={classes}
      disabled={softDisabled ? undefined : isDisabled}
      aria-disabled={softDisabled ? true : undefined}
      aria-busy={isLoading}
      title={softDisabled ? (title ?? disabledReason) : title}
      onClick={handleClick}
      {...rest}
    >
      {isLoading ? (
        <span className={styles.spinner} aria-hidden="true">
          <Loader size="sm" />
        </span>
      ) : null}
      {leftIcon ? <span className={styles.icon}>{leftIcon}</span> : null}
      <span className={styles.label}>{children}</span>
      {rightIcon ? <span className={styles.icon}>{rightIcon}</span> : null}
    </button>
  );
});
